import React from 'react';

import PriceDistributionView from '../../components/PriceDistributionView';

const ExpandedRunHistoryRows = ({
  controller,
  run,
  res,
  ridx,
  hist,
  hidx,
  runMinConfidenceThreshold,
}) => {
  const {
    getFilteredItemEntries,
    getResultImageUrls,
    getDisabledImageKeySet,
    toFiniteNumber,
    getResultTokenCounts,
    getResultAttemptSourceInfo,
    computeAccuracyRatio,
    normalizeImageKey,
    setSelectedImage,
    setSelectedImages,
    setSelectedLotNum,
    setSelectedItems,
    getResultLotNumber,
    formatConfidencePercent,
    getReviewNoteDraftKey,
    getTrustedReviewNote,
    reviewNoteDrafts,
    setReviewNoteDrafts,
    savingReviewNotes,
    saveReviewNote,
    lotNoteDrafts,
    setLotNoteDrafts,
    savingLotNotes,
    saveLotReviewNote,
    getSourceBadgeClassName,
    getSourceDisplayLabel,
    toFiniteCount,
    formatTokenCount,
    formatCostDisplay,
  } = controller;

  const runId = String(run?.run_id || '').trim();
  const histItemEntries = getFilteredItemEntries(hist?.items, runMinConfidenceThreshold);
  if (histItemEntries.length === 0) return null;

  const histItemCount = histItemEntries.length;
  const firstHistItem = (histItemEntries.find((entry) => entry.item)?.item) || null;
  const histImageUrls = getResultImageUrls(hist, firstHistItem);
  const histDisabledKeys = getDisabledImageKeySet(hist);
  const visibleHistItemEntries = histItemEntries.filter(
    (entry) => entry.item && typeof entry.item === 'object',
  );
  const histEstSum = histItemEntries.reduce(
    (sum, entry) => (
      entry.item && typeof entry.item === 'object'
        ? (sum + (toFiniteNumber(entry?.item?.estimated) || 0))
        : sum
    ),
    0,
  );
  const histActual = visibleHistItemEntries
    .map((entry) => toFiniteNumber(entry?.item?.actual))
    .find((value) => value !== null) ?? null;
  const histTokens = getResultTokenCounts(hist);
  const histSourceInfo = getResultAttemptSourceInfo(hist);
  const histAcc = computeAccuracyRatio(histActual, histEstSum);
  const lotNumberForHistory = getResultLotNumber(res, firstHistItem) || getResultLotNumber(res) || ridx;

  return (
    <React.Fragment>
      {histItemEntries.map(({ item: hItem, originalIndex: histOriginalIndex }, hiidx) => {
        const hi = hItem && typeof hItem === 'object' ? hItem : {};
        const isHistFilteredOutRow = histOriginalIndex === null;
        const hasConcreteHistIndex = Number.isFinite(Number(histOriginalIndex));
        const hiEst = toFiniteNumber(hi.estimated);

        const histReviewDraftKey = hasConcreteHistIndex
          ? getReviewNoteDraftKey({
              runId,
              lotNumber: lotNumberForHistory,
              resultIndex: ridx,
              itemIndex: histOriginalIndex,
              historyIndex: hidx,
            })
          : '';
        const histPersistedNote = hasConcreteHistIndex ? getTrustedReviewNote(hi) : '';
        const histNoteValue = hasConcreteHistIndex && Object.prototype.hasOwnProperty.call(reviewNoteDrafts, histReviewDraftKey)
          ? reviewNoteDrafts[histReviewDraftKey]
          : histPersistedNote;
        const histIsSaving = hasConcreteHistIndex ? !!savingReviewNotes[histReviewDraftKey] : false;
        const histHasChanges = hasConcreteHistIndex ? (histNoteValue !== histPersistedNote) : false;
        const histCanSave = hasConcreteHistIndex && Boolean(runId && lotNumberForHistory);

        return (
          <tr key={`${ridx}-hist-${hidx}-${histOriginalIndex}-${hiidx}`} className={`bg-ai/10 ${hiidx < histItemCount - 1 ? '' : 'border-b border-ai/30'}`}>
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top bg-ai/15" rowSpan={histItemCount}>
                <div className="flex flex-col items-start gap-0.5">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-ai-deep text-white font-bold text-[8px] shrink-0">
                    {hidx + 1}
                  </span>
                  {hist.rerun_timestamp && (
                    <span className="text-[8px] text-ai leading-tight text-center">
                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      <span className="block">{new Date(hist.rerun_timestamp).getFullYear()}</span>
                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                  )}
                </div>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                <div className="flex flex-wrap gap-1">
                  {histImageUrls.map((url, huidx) => {
                    const hImgKey = normalizeImageKey(url);
                    const hIsDisabled = histDisabledKeys.has(hImgKey);
                    return (
                      <div key={huidx} className="relative">
                        <img
                          src={url}
                          alt={`hist ${huidx + 1}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(url);
                            setSelectedImages(histImageUrls);
                            setSelectedLotNum(lotNumberForHistory);
                            setSelectedItems(hist.items);
                          }}
                          className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ${hIsDisabled ? 'grayscale opacity-35' : ''}`}
                        />
                        {hIsDisabled && (
                          <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold text-white bg-deep/60 rounded">OFF</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </td>
            )}
            <td className="px-1.5 py-1 border-r border-hairline font-medium">
              {isHistFilteredOutRow ? (
                <span className="text-muted">-</span>
              ) : (
                <div className="break-words" title={hi.item || hi.item_name || '-'}>{hi.item || hi.item_name || '-'}</div>
              )}
            </td>
            <td className="px-1.5 py-1 border-r border-hairline">
              {isHistFilteredOutRow ? <span className="text-muted">-</span> : (hiEst === null ? '-' : `$${hiEst.toFixed(2)}`)}
            </td>
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top font-bold" rowSpan={histItemCount}>
                ${histEstSum.toFixed(2)}
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                {histActual === null ? '-' : `$${histActual.toFixed(2)}`}
              </td>
            )}
            <td className="px-1.5 py-1 border-r border-hairline text-muted">
              {isHistFilteredOutRow ? (
                <div className="text-[10px] text-muted italic">Filtered out by confidence</div>
              ) : (
                (() => {
                  const hCondition = hi?.condition || 'Unknown';
                  const hPriceSource = hi?.price_source || 'model_knowledge';
                  const hConfidenceLabel = formatConfidencePercent(hi?.confidence_score, '-');
                  const hSourceLabel = hPriceSource === 'ebay_rag' ? 'eBay RAG' : hPriceSource === 'image' ? 'Image' : 'Model Knowledge';
                  const hSourceBg = hPriceSource === 'ebay_rag' ? 'bg-teal/10 text-teal' : hPriceSource === 'image' ? 'bg-warn/10 text-warn' : 'bg-surface-2 text-muted';
                  const hCondBg = hCondition === 'New' ? 'bg-positive/10 text-positive' : hCondition === 'Damaged' || hCondition === 'For Parts' ? 'bg-danger/10 text-danger' : hCondition === 'Unknown' ? 'bg-surface-2 text-muted' : 'bg-warn/10 text-warn';
                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Condition:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hCondBg}`}>{hCondition}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Price Src:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hSourceBg}`}>{hSourceLabel}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Conf:</span><span className="inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold bg-ai/10 text-ai">{hConfidenceLabel}</span></div>
                      <PriceDistributionView
                        rangeValue={hi?.estimated_value_range}
                        distributionValue={hi?.price_estimate_distribution}
                        maxBins={Number.MAX_SAFE_INTEGER}
                        compact
                        className="mt-0.5"
                      />
                    </div>
                  );
                })()
              )}
            </td>
            <td className="px-1.5 py-1 border-r border-hairline align-top">
              {isHistFilteredOutRow ? (
                <span className="text-[10px] text-muted">-</span>
              ) : (
                <div className="space-y-1">
                  <textarea
                    value={histNoteValue}
                    onChange={(event) => {
                      const nextValue = event.target.value.slice(0, 4000);
                      setReviewNoteDrafts((previous) => ({ ...previous, [histReviewDraftKey]: nextValue }));
                    }}
                    maxLength={4000}
                    placeholder="Add subitem note..."
                    className="w-full min-h-[48px] rounded border border-ai/30 px-1.5 py-1 text-[10px] outline-none focus:border-ai bg-ai/5 text-ink placeholder:text-muted"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted">{histNoteValue.length}/4000</span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!histCanSave || histIsSaving || !histHasChanges) return;
                        saveReviewNote({
                          runId,
                          lotNumber: lotNumberForHistory,
                          resultIndex: ridx,
                          itemIndex: histOriginalIndex,
                          historyIndex: hidx,
                          reviewNote: histNoteValue,
                        });
                      }}
                      disabled={!histCanSave || histIsSaving || !histHasChanges}
                      className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!histCanSave || histIsSaving || !histHasChanges ? 'bg-surface-2 text-muted cursor-not-allowed border border-hairline' : 'bg-ai-deep text-white hover:bg-ai/10 border border-ai'}`}
                    >
                      {histIsSaving ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </div>
              )}
            </td>
            {hiidx === 0 && (() => {
              const histLotDraftKey = `lot-${runId}-${lotNumberForHistory || ridx}-${ridx}-h${hidx}`;
              const histPersistedLotNote = (typeof hist.lot_review_note === 'string') ? hist.lot_review_note : '';
              const histLotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, histLotDraftKey) ? lotNoteDrafts[histLotDraftKey] : histPersistedLotNote;
              const histIsSavingLotNote = !!savingLotNotes[histLotDraftKey];
              const histHasLotNoteChanges = histLotNoteValue !== histPersistedLotNote;
              const histLotCanSave = Boolean(runId && lotNumberForHistory);
              return (
                <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                  <div className="space-y-1">
                    <textarea
                      value={histLotNoteValue}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, 4000);
                        setLotNoteDrafts((prev) => ({ ...prev, [histLotDraftKey]: v }));
                      }}
                      maxLength={4000}
                      placeholder="Add item note..."
                      className="w-full min-h-[48px] rounded border border-ai/30 px-1.5 py-1 text-[10px] outline-none focus:border-ai bg-ai/5 text-ink placeholder:text-muted"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted">{histLotNoteValue.length}/4000</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges) return;
                          saveLotReviewNote({
                            runId,
                            lotNumber: lotNumberForHistory,
                            resultIndex: ridx,
                            historyIndex: hidx,
                            reviewNote: histLotNoteValue,
                          });
                        }}
                        disabled={!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges}
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges ? 'bg-surface-2 text-muted cursor-not-allowed border border-hairline' : 'bg-ai-deep text-white hover:bg-ai/10 border border-ai'}`}
                      >
                        {histIsSavingLotNote ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </td>
              );
            })()}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                {histAcc !== null ? (
                  <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${histAcc > 0.8 ? 'bg-positive/10 text-positive' : histAcc > 0.5 ? 'bg-warn/10 text-warn' : 'bg-danger/10 text-danger'}`}>
                    {(histAcc * 100).toFixed(1)}%
                  </span>
                ) : '-'}
              </td>
            )}
            {hiidx === 0 && (() => {
              const histDurSec = toFiniteNumber(hist.duration);
              const histDurMs = toFiniteNumber(hist.duration_ms);
              const histDur = histDurMs > 0 ? histDurMs / 1000 : (histDurSec > 0 ? histDurSec : 0);
              const liveDur = toFiniteNumber(hist.last_live_duration);
              const displayDur = histDur > 0 ? histDur : liveDur;
              return (
                <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                  {displayDur != null && displayDur > 0 ? `${Number(displayDur).toFixed(1)}s` : '-'}
                </td>
              );
            })()}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                <span
                  className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(histSourceInfo.finalSource, histSourceInfo.finalCacheHit)}`}
                  title={histSourceInfo.cacheKey || undefined}
                >
                  {histSourceInfo.finalCacheHit === null ? getSourceDisplayLabel(histSourceInfo.finalSource) : (histSourceInfo.finalCacheHit ? 'CACHE' : 'LIVE')}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(histTokens.input) !== null ? 'bg-teal/10 text-teal' : 'bg-surface-2 text-muted'}`}>
                  {formatTokenCount(histTokens.input)}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={histItemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(histTokens.output) !== null ? 'bg-ai/10 text-ai' : 'bg-surface-2 text-muted'}`}>
                  {formatTokenCount(histTokens.output)}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 align-top" rowSpan={histItemCount}>
                {formatCostDisplay(hist.cost)}
              </td>
            )}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

export default ExpandedRunHistoryRows;
